import { useCallback, useEffect, useState } from "react";
import { ApiError, sanitizeApiErrorMessage } from "@/shared/lib/apiClient";
import { fetchAdminInvoices } from "../api/adminApi";
import { fetchAuditorInvoices } from "../api/auditorApi";
import type { CompanyBillingInvoice } from "../api/types";
import { getAuthToken, getStoredAccountType } from "../auth";
import type { Invoice, InvoiceReviewStatus, InvoiceStatus } from "../types";
import { resolveInvoiceNatureKey } from "../components/InvoiceNatureBadge";

export type AdminInvoicesFilter = {
  /** pending | approved | rejected | audited | all */
  mode?: "pending" | "approved" | "rejected" | "audited" | "all";
};

function mapDisplayToInvoice(row: CompanyBillingInvoice): Invoice {
  const created = row.created_at ? new Date(row.created_at) : null;
  const submittedDate = created
    ? created.toLocaleDateString("fr-FR")
    : "—";

  let status: InvoiceStatus = "pending";
  let reviewStatus: InvoiceReviewStatus = "submitted";

  const display = row.display_status;
  if (display === "approved") {
    status = "approved";
  } else if (display === "rejected") {
    status = "rejected";
  } else if (display === "submitted") {
    reviewStatus = "submitted";
  } else if (display === "pendingAudit") {
    reviewStatus = "pendingAudit";
  } else if (display === "awaitingClaims") {
    reviewStatus = "awaitingClaims";
  } else {
    reviewStatus = "inReview";
  }

  const raw = (row.status || "").toLowerCase().replace(/\s+/g, "_");
  if (raw === "submitted") reviewStatus = "submitted";
  if ((raw === "under_review" || raw === "auditstarted") && display !== "awaitingClaims") reviewStatus = "inReview";
  if (raw === "pending_audit") reviewStatus = "pendingAudit";

  const nature = resolveInvoiceNatureKey({
    invoiceNature: row.invoice_nature,
    isForfaitMensuel: row.is_forfait_mensuel,
  });

  return {
    id: `admin-inv-${row.id}`,
    displayId: row.id,
    reference: row.reference,
    hospital: row.hospital,
    company: row.company || "—",
    amount: row.amount_usd,
    submittedDate,
    period: row.period_label || submittedDate,
    delayDays: 0,
    status,
    reviewStatus,
    createdAt: row.created_at ?? "",
    fileName: row.label || row.reference,
    urgent: false,
    invoiceNature: nature,
    isForfaitMensuel: nature === "forfait_mensuel",
    insurer: row.insurer ?? "",
    deadline: row.deadline ?? null,
    batchNumber: row.batch_number ?? null,
    dueDate: row.due_date ?? null,
  };
}

function queryForMode(mode: AdminInvoicesFilter["mode"]): {
  status?: string;
  all?: boolean;
} {
  switch (mode) {
    case "approved":
      return { status: "approved" };
    case "rejected":
      return { status: "rejected" };
    case "audited":
      return { status: "audited" };
    case "pending":
      return { status: "pending" };
    case "all":
    default:
      return { all: true };
  }
}

async function fetchInvoicesForRole(
  mode: AdminInvoicesFilter["mode"],
): Promise<CompanyBillingInvoice[]> {
  const accountType = getStoredAccountType();
  const query = queryForMode(mode);

  if (accountType === "auditor") {
    if (query.all) {
      return fetchAuditorInvoices({ status: "all" as never });
    }
    return fetchAuditorInvoices({
      status: (query.status ?? "pending") as NonNullable<
        Parameters<typeof fetchAuditorInvoices>[0]["status"]
      >,
    });
  }

  return fetchAdminInvoices(query);
}

export function useAdminInvoices(filter: AdminInvoicesFilter = { mode: "all" }) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setInvoices([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const rows = await fetchInvoicesForRole(filter.mode);
      setInvoices(rows.map(mapDisplayToInvoice));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(null);
        return;
      }
      setError(
        sanitizeApiErrorMessage(
          err instanceof Error ? err.message : "Impossible de charger les factures.",
        ),
      );
      setInvoices([]);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, [filter.mode]);

  useEffect(() => {
    void load();
  }, [load]);

  return { invoices, loading, error, usingApi, reload: load };
}
